<template>
  <div ref="root" class="form-layout-menu">
    <button
      type="button"
      class="btn btn-sm btn-outline-primary"
      :title="$t('Form columns')"
      @click="open = !open"
    >
      <i class="fas fa-columns"></i>
      {{ columns ? columns + ' ' + $t('col.') : $t('Columns') }}
    </button>

    <div v-if="open" class="form-layout-menu__panel">
      <div class="form-layout-menu__title">{{ $t('Form columns') }}</div>
      <div class="form-layout-menu__options">
        <button
          type="button"
          class="form-layout-menu__option"
          :class="{ 'is-active': !columns }"
          :disabled="saving"
          @click="choose(null)"
        >
          {{ $t('Automatic') }}
        </button>
        <button
          v-for="n in options"
          :key="n"
          type="button"
          class="form-layout-menu__option"
          :class="{ 'is-active': columns === n }"
          :disabled="saving"
          @click="choose(n)"
        >
          {{ n }}
        </button>
      </div>

      <button
        v-if="columns"
        type="button"
        class="btn btn-sm btn-outline-secondary w-100 mt-2"
        :disabled="saving"
        @click="editWidths"
      >
        <i class="fas fa-arrows-alt-h"></i> {{ $t('Adjust column widths') }}
      </button>

      <label v-if="columns" class="form-layout-menu__share">
        <input type="checkbox" :checked="isPublic" :disabled="saving" @change="toggleShare($event.target.checked)" />
        {{ $t('Share with other users') }}
      </label>

      <div class="form-layout-menu__title mt-2">{{ $t('Layouts from other users') }}</div>
      <div v-if="!shared.length" class="form-layout-menu__empty">{{ $t('No shared layouts yet') }}</div>
      <div v-for="layout in shared" :key="layout._id" class="form-layout-menu__shared">
        <span>
          {{ layout.owner ? layout.owner.name : '—' }}
          <small class="text-muted">· {{ layout.columns }} {{ $t('col.') }}</small>
        </span>
        <button type="button" class="btn btn-sm btn-link p-0" :disabled="saving" @click="choose(layout.columns, layout.columnWidth)">
          {{ $t('Import') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";

// Cuántas columnas usa el formulario para este usuario y modelo, y cuánto
// miden (todas lo mismo). Se guarda en el back (uno por usuario y modelo);
// compartido, otro usuario lo importa como copia suya. Emite `change` con
// { columns, columnWidth } o null (automático), y `edit-widths` para que la
// página muestre la regla; la página guarda el ancho con saveColumnWidth().
export default {
  name: "FormLayoutMenu",
  props: {
    modelName: { type: String, required: true },
  },
  data() {
    return {
      open: false,
      saving: false,
      columns: null,
      columnWidth: null,
      isPublic: false,
      shared: [],
      options: [1, 2, 3, 4, 5, 6, 8],
    };
  },
  watch: {
    modelName: { handler: "load", immediate: true },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick, true);
  },
  methods: {
    url() {
      return `/api/gotrading/form-layouts/${encodeURIComponent(this.modelName)}`;
    },
    onDocumentClick(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.open = false;
      }
    },
    apply(mine) {
      this.columns = mine ? mine.columns : null;
      this.columnWidth = mine && mine.columnWidth ? mine.columnWidth : null;
      this.isPublic = !!(mine && mine.isPublic);
      this.$emit("change", this.columns ? { columns: this.columns, columnWidth: this.columnWidth } : null);
    },
    editWidths() {
      this.open = false;
      this.$emit("edit-widths");
    },
    async saveColumnWidth(columnWidth) {
      if (!this.columns) return;
      this.saving = true;
      try {
        const { data } = await wencesApi.put(this.url(), {
          columns: this.columns,
          columnWidth: columnWidth || null,
        });
        this.apply(data);
      } catch (error) {
        console.error("Error guardando el ancho de las columnas:", error);
        this.apply({ columns: this.columns, columnWidth: this.columnWidth, isPublic: this.isPublic });
      } finally {
        this.saving = false;
      }
    },
    async load() {
      if (!this.modelName) return;
      try {
        const { data } = await wencesApi.get(this.url());
        this.shared = data.shared || [];
        this.apply(data.mine);
      } catch (error) {
        console.error("Error cargando el diseño del formulario:", error);
        this.apply(null);
      }
    },
    async choose(columns, columnWidth) {
      this.saving = true;
      try {
        if (columns) {
          const body = { columns };
          if (columnWidth !== undefined) body.columnWidth = columnWidth || null;
          const { data } = await wencesApi.put(this.url(), body);
          this.apply(data);
        } else {
          await wencesApi.delete(this.url());
          this.apply(null);
        }
      } catch (error) {
        console.error("Error guardando el diseño del formulario:", error);
      } finally {
        this.saving = false;
      }
    },
    async toggleShare(isPublic) {
      this.saving = true;
      try {
        const { data } = await wencesApi.put(this.url(), { columns: this.columns, isPublic });
        this.apply(data);
      } catch (error) {
        console.error("Error compartiendo el diseño del formulario:", error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* La grilla de columnas solo corre desde 768px: en el teléfono no sirve. */
@media (max-width: 767.98px) {
  .form-layout-menu {
    display: none !important;
  }
}

.form-layout-menu {
  position: relative;
}

.form-layout-menu__panel {
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  z-index: 1060;
  width: 17rem;
  padding: 0.75rem;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  box-shadow: 0 12px 32px -8px rgba(16, 24, 40, 0.18);
}

.form-layout-menu__title {
  margin-bottom: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.form-layout-menu__options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.form-layout-menu__option {
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 6px;
}

.form-layout-menu__option:hover:not(:disabled) {
  border-color: var(--bs-primary);
}

.form-layout-menu__option.is-active {
  color: #fff;
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.form-layout-menu__share {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.6rem 0 0;
  font-size: 0.85rem;
  cursor: pointer;
}

.form-layout-menu__empty {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
}

.form-layout-menu__shared {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.2rem 0;
  font-size: 0.85rem;
}
</style>
